// InvitationConfirmPage.js - Improved version with comprehensive error handling
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Clock, User, Building, Shield, RefreshCw, Eye } from 'lucide-react';


const InvitationConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if (token) {
      fetchInvitationDetails();
    } else {
      setError('Invalid invitation link - no token provided');
      setLoading(false);
    }
  }, [token]);

  // Countdown timer for redirect
  useEffect(() => {
    if (success && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (success && redirectCountdown === 0) {
      navigate('/', { 
        state: { 
          message: 'Invitation confirmed! Please log in with your credentials.',
          email: invitation?.email 
        } 
      });
    }
  }, [success, redirectCountdown, navigate, invitation]);

  const fetchInvitationDetails = async () => {
    try {
      // console.log('Fetching invitation for token:', token.substring(0, 10) + '...');
      
      // Build the correct API URL
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 
                      (process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:6532' : '');
      
      const apiUrl = `${baseUrl}/access/getInvitation/${encodeURIComponent(token)}`;
      // console.log('API URL:', apiUrl);
      
      // Store debug info for potential troubleshooting
      const debugData = {
        requestUrl: apiUrl,
        token: token.substring(0, 10) + '...',
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV
      };
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Add response info to debug data
      debugData.responseStatus = response.status;
      debugData.responseHeaders = Object.fromEntries(response.headers.entries());
      
      // console.log('Response status:', response.status);
      // console.log('Response headers:', debugData.responseHeaders);
      
      // Check content type BEFORE trying to parse
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/json')) {
        // Get the actual response text for debugging
        const textResponse = await response.text();
        debugData.actualResponse = textResponse.substring(0, 500);
        debugData.contentType = contentType;
        
        console.error('Expected JSON but got:', contentType);
        console.error('Response preview:', textResponse.substring(0, 200));
        
        setDebugInfo(debugData);
        
        // Provide specific error messages based on what we received
        if (textResponse.includes('ALBURY') || textResponse.includes(',')) {
          throw new Error(
            `Server returned CSV data instead of invitation details. This suggests a routing issue where your request is hitting a different endpoint that exports site data. ` +
            `Expected: /access/getInvitation/${token}, but got CSV response.`
          );
        } else if (textResponse.includes('<html>') || textResponse.includes('<!DOCTYPE')) {
          throw new Error(
            `Server returned HTML instead of JSON. This usually means the API endpoint doesn't exist or there's a routing configuration issue. ` +
            `Check if the /access/getInvitation/${token} endpoint is properly configured.`
          );
        } else {
          throw new Error(
            `Server returned unexpected content type: ${contentType || 'unknown'}. Expected: application/json. ` +
            `This indicates an API configuration problem.`
          );
        }
      }
      
      // Try to parse JSON
      let data;
      try {
        const responseText = await response.text();
        debugData.actualResponse = responseText;
        data = JSON.parse(responseText);
      } catch (parseError) {
        debugData.parseError = parseError.message;
        setDebugInfo(debugData);
        console.error('JSON parse error:', parseError);
        throw new Error(`Invalid JSON response from server. Parse error: ${parseError.message}`);
      }
      
      // Handle HTTP error status codes
      if (!response.ok) {
        debugData.errorResponse = data;
        setDebugInfo(debugData);
        
        // Handle specific error cases
        if (data.status === 'already_confirmed') {
          setAlreadyConfirmed(true);
          setInvitation(data.invitation || { email: 'unknown', business: 'the system' });
          return;
        } else if (data.status === 'expired') {
          throw new Error('This invitation has expired. Please request a new invitation from your administrator.');
        } else if (data.status === 'cancelled') {
          throw new Error('This invitation has been cancelled. Please contact your administrator.');
        } else {
          throw new Error(data.error || `Server error: ${response.status}`);
        }
      }
      
      // Success case
      debugData.successResponse = data;
      setDebugInfo(debugData);
      setInvitation(data);
      
    } catch (error) {
      console.error('Error fetching invitation:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmInvitation = async () => {
    try {
      setConfirming(true);
      setError(null);
      
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 
                      (process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:6532' : '');

      const response = await fetch(`${baseUrl}/access/confirmInvitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ token })
      });
      
      // Check content type for confirm endpoint too
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Confirm endpoint returned non-JSON:', textResponse.substring(0, 200));
        throw new Error('Server configuration error: Confirm endpoint returned unexpected content type');
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to confirm invitation');
      }
      
      setSuccess(true);
      setRedirectCountdown(5);
      
    } catch (error) {
      console.error('Error confirming invitation:', error);
      setError(error.message);
    } finally {
      setConfirming(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'tla': return { backgroundColor: 'rgba(168, 85, 247, 0.2)', color: '#c4b5fd', border: '1px solid rgba(168, 85, 247, 0.3)' };
      case 'admin': return { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)' };
      case 'supervisor': return { backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)' };
      case 'technician': return { backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#86efac', border: '1px solid rgba(34, 197, 94, 0.3)' };
      default: return { backgroundColor: 'rgba(156, 163, 175, 0.2)', color: '#d1d5db', border: '1px solid rgba(156, 163, 175, 0.3)' };
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      // backgroundColor: '#f8fafc'
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '16px',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      padding: '40px',
      maxWidth: '600px',
      width: '100%',
      textAlign: 'center'
    },
    header: {
      marginBottom: '32px'
    },
    icon: {
      width: '64px',
      height: '64px',
      margin: '0 auto 16px',
      color: success ? '#10b981' : error ? '#ef4444' : '#667eea'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: '16px',
      color: '#6b7280',
      margin: '0'
    },
    invitationDetails: {
      backgroundColor: '#f8fafc',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '32px',
      textAlign: 'left'
    },
    detailRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
      gap: '12px'
    },
    detailIcon: {
      width: '20px',
      height: '20px',
      color: '#6b7280'
    },
    detailLabel: {
      fontWeight: '600',
      color: '#374151',
      minWidth: '80px'
    },
    detailValue: {
      color: '#6b7280',
      flex: 1
    },
    roleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: '500'
    },
    sitesContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    siteChip: {
      backgroundColor: '#e5e7eb',
      color: '#374151',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      display: 'inline-block',
      marginRight: '4px',
      marginBottom: '4px'
    },
    button: {
      backgroundColor: '#16a34a',
      color: 'white',
      border: 'none',
      padding: '16px 32px',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      marginBottom: '16px'
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed'
    },
    secondaryButton: {
      backgroundColor: '#6b7280',
      fontSize: '14px',
      padding: '8px 16px',
      marginTop: '16px'
    },
    errorMessage: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      padding: '16px',
      color: '#dc2626',
      marginBottom: '24px',
      textAlign: 'left'
    },
    successMessage: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: '8px',
      padding: '16px',
      color: '#059669',
      marginBottom: '24px'
    },
    alreadyConfirmedMessage: {
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '8px',
      padding: '16px',
      color: '#1d4ed8',
      marginBottom: '24px'
    },
    debugSection: {
      backgroundColor: '#f3f4f6',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '16px',
      marginTop: '16px',
      textAlign: 'left',
      fontSize: '12px'
    },
    debugTitle: {
      fontWeight: '600',
      marginBottom: '8px',
      color: '#374151'
    },
    debugContent: {
      fontFamily: 'monospace',
      color: '#6b7280',
      whiteSpace: 'pre-wrap',
      maxHeight: '200px',
      overflow: 'auto'
    },
    countdownText: {
      fontSize: '16px',
      color: '#374151',
      fontWeight: '500',
      marginTop: '16px'
    },
    loadingSpinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid currentColor',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    footer: {
      fontSize: '14px',
      color: '#9ca3af',
      marginTop: '24px'
    }
  };

  // Add CSS for spinner animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <Clock style={styles.icon} />
            <h1 style={styles.title}>Loading Invitation</h1>
            <p style={styles.subtitle}>Please wait while we verify your invitation...</p>
          </div>
          <div style={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  // Handle already confirmed state
  if (alreadyConfirmed) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <CheckCircle style={{...styles.icon, color: '#3b82f6'}} />
            <h1 style={styles.title}>Already Confirmed</h1>
            <p style={styles.subtitle}>This invitation has already been processed</p>
          </div>
          
          <div style={styles.alreadyConfirmedMessage}>
            <strong>Good news!</strong> This invitation has already been confirmed and your account is active.
            You can proceed directly to log in.
          </div>
          
          <button
            style={styles.button}
            onClick={() => navigate('/', { 
              state: { 
                message: 'Your account is already active. Please log in.',
                email: invitation?.email 
              } 
            })}
            onMouseOver={(e) => e.target.style.backgroundColor = '#15803d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#16a34a'}
          >
            <User style={{width: '20px', height: '20px'}} />
            Go to Login
          </button>
          
          <div style={styles.footer}>
            If you're having trouble logging in, please contact your administrator.
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <XCircle style={styles.icon} />
            <h1 style={styles.title}>Invitation Error</h1>
            <p style={styles.subtitle}>There was a problem with your invitation link</p>
          </div>
          
          <div style={styles.errorMessage}>
            <strong>Error:</strong> {error}
          </div>

          <button
            style={{...styles.button, backgroundColor: '#3b82f6'}}
            onClick={fetchInvitationDetails}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            <RefreshCw style={{width: '20px', height: '20px'}} />
            Try Again
          </button>

          {debugInfo && (
            <>
              <button
                style={{...styles.button, ...styles.secondaryButton}}
                onClick={() => setShowDebug(!showDebug)}
              >
                <Eye style={{width: '16px', height: '16px'}} />
                {showDebug ? 'Hide' : 'Show'} Debug Info
              </button>

              {showDebug && (
                <div style={styles.debugSection}>
                  <div style={styles.debugTitle}>Debug Information:</div>
                  <div style={styles.debugContent}>
                    {JSON.stringify(debugInfo, null, 2)}
                  </div>
                </div>
              )}
            </>
          )}
          
          <div style={styles.footer}>
            If you continue to see this error, please contact the person who invited you and share the debug information above.
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <CheckCircle style={styles.icon} />
            <h1 style={styles.title}>Invitation Confirmed!</h1>
            <p style={styles.subtitle}>Welcome to {invitation.business}</p>
          </div>
          
          <div style={styles.successMessage}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'}}>
              <CheckCircle style={{width: '20px', height: '20px', color: '#059669'}} />
              <strong>Success!</strong>
            </div>
            Your invitation has been successfully confirmed. You now have access to {invitation.business} with <strong>{invitation.role}</strong> privileges.
          </div>
          
          <div style={styles.countdownText}>
            Redirecting to login in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}...
          </div>
          
          <button
            style={{...styles.button, backgroundColor: '#3b82f6', marginTop: '16px'}}
            onClick={() => navigate('/', { 
              state: { 
                message: 'Invitation confirmed! Please log in with your credentials.',
                email: invitation.email 
              } 
            })}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            <User style={{width: '20px', height: '20px'}} />
            Go to Login Now
          </button>
          
          <div style={styles.footer}>
            Your account has been created and is ready to use!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <AlertTriangle style={styles.icon} />
          <h1 style={styles.title}>Confirm Your Invitation</h1>
          <p style={styles.subtitle}>You've been invited to join {invitation.business}</p>
        </div>

        <div style={styles.invitationDetails}>
          <div style={styles.detailRow}>
            <User style={styles.detailIcon} />
            <span style={styles.detailLabel}>Name:</span>
            <span style={styles.detailValue}>{invitation.name}</span>
          </div>
          
          <div style={styles.detailRow}>
            <Building style={styles.detailIcon} />
            <span style={styles.detailLabel}>Business:</span>
            <span style={styles.detailValue}>{invitation.business}</span>
          </div>
          
          <div style={styles.detailRow}>
            <Shield style={styles.detailIcon} />
            <span style={styles.detailLabel}>Role:</span>
            <span style={{...styles.roleBadge, ...getRoleColor(invitation.role)}}>
              {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
            </span>
          </div>
          
          {invitation.sites && invitation.sites.length > 0 && (
            <div style={styles.detailRow}>
              <div style={styles.detailIcon}>🏢</div>
              <span style={styles.detailLabel}>Sites:</span>
              <div style={styles.sitesContainer}>
                {invitation.sites.map((site, index) => (
                  <span key={index} style={styles.siteChip}>{site}</span>
                ))}
              </div>
            </div>
          )}
          
          <div style={styles.detailRow}>
            <div style={styles.detailIcon}>👤</div>
            <span style={styles.detailLabel}>Invited by:</span>
            <span style={styles.detailValue}>{invitation.invitedByEmail}</span>
          </div>
        </div>

        <button
          style={{
            ...styles.button,
            ...(confirming ? styles.buttonDisabled : {})
          }}
          onClick={confirmInvitation}
          disabled={confirming}
          onMouseOver={(e) => !confirming && (e.target.style.backgroundColor = '#15803d')}
          onMouseOut={(e) => !confirming && (e.target.style.backgroundColor = '#16a34a')}
        >
          {confirming ? (
            <>
              <div style={styles.loadingSpinner}></div>
              Confirming...
            </>
          ) : (
            <>
              <CheckCircle style={{width: '20px', height: '20px'}} />
              Confirm & Join {invitation.business}
            </>
          )}
        </button>

        <div style={styles.footer}>
          By confirming this invitation, you agree to join {invitation.business} with the specified role and permissions.
        </div>
      </div>
    </div>
  );
};

export default InvitationConfirmPage;